import {
  FileUploadPattern,
  FormDate,
  FormGroup,
  FormRadioGroup,
  Heading,
  InPageAlert,
  Input,
  UploadedItem,
} from '@snsw/react-component-library';
import React, { useEffect, useState } from 'react';
import { PLANT_API_BASE_URL, radioOptionsYesNo } from 'shared/constants/constants';
import { isEmpty, validateExt } from 'shared/utils/Validation';
import styles from './AddInspectionCertificate.module.scss';
import moment from 'moment';
import axios from 'axios';
import { PlantItemDetailsPage } from 'plantItemNew/constants/plantItemDetailsPage';
import { sanitizeHtml } from 'shared/utils/Helpers';

export const InspectionDocumentsUpload = React.forwardRef((props, ref) => {
  const {
    id = '',
    value = {},
    onChange = () => {},
    validateInspectiondate = () => {},
    applicationId,
    onErrorChange = () => {},
    warnInspectiondate = () => {},
    supportingDocumentCode,
    description,
    labels = PlantItemDetailsPage.relatedDetails.inspectionDocumentUpload,
    maxFiles = 10,
  } = props;

  const {
    competentPerson: savedCompetentPerson = '',
    inspectionDate: savedInspectionDate = '',
    anySpecialConditions: savedAnySpecialConditions = '',
    specialConditions: savedSpecialConditions = '',
    uploadedFiles: savedUploadedFiles = [],
  } = value;

  const [competentPerson, setCompetentPerson] = useState(savedCompetentPerson);
  const [inspectionDate, setInspectionDate] = useState(savedInspectionDate);
  const [anySpecialConditions, setAnySpecialConditions] = useState(savedAnySpecialConditions);
  const [specialConditions, setSpecialConditions] = useState(savedSpecialConditions);
  const [uploadedFiles, setUploadedFiles] = useState(savedUploadedFiles);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);
  const [inspectionDateWarning, setInspectionDateWarning] = useState();

  const validators = [
    {
      fieldName: `${id}_competentPerson`,
      fieldValue: competentPerson,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: `${id}_inspectionDate`,
      fieldValue: inspectionDate,
      isValid: (v) => /^\d{4}-\d{2}-\d{2}$/.test(v) && moment(v).isBefore(moment()) && !validateInspectiondate(v),
      errorMessage: (v) => validateInspectiondate(v) || 'Please enter valid date',
    },
    {
      fieldName: `${id}_anySpecialConditions`,
      fieldValue: anySpecialConditions,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: `${id}_conditions`,
      fieldValue: specialConditions,
      isValid: (v) => !isEmpty(v) || isEmpty(anySpecialConditions) || anySpecialConditions === 'no',
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: `${id}_upload`,
      fieldValue: uploadedFiles,
      isValid: (v) => v.length > 0,
      errorMessage: () => 'Please attach file',
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        let isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);

        setShowErrors(!isCurrentComponentValid);

        return isCurrentComponentValid;
      },
    };
  }

  let attachmentEndpointUrl = `${PLANT_API_BASE_URL}/application/${applicationId}/attachments`;

  const attachmentApi = {
    uploadFile: async (file) => {
      const content = new FormData();
      content.append('file', file);
      const options = {
        url: `${attachmentEndpointUrl}/${file.name}`,
        method: 'post',
        data: content,
      };
      const response = await axios(options);
      return response.data;
    },
    removeFile: async (fileId) => {
      const response = await axios.delete(`${attachmentEndpointUrl}/${fileId}`);
      return response.data;
    },
  };

  useEffect(() => {
    let result = {
      competentPerson,
      inspectionDate,
      anySpecialConditions,
      ...(anySpecialConditions === 'yes' && { specialConditions }),
      ...(anySpecialConditions === 'no' && { specialConditions: '' }),
      uploadedFiles,
      attachments: formatAttachments(uploadedFiles),
    };

    onChange(result);

    setInspectionDateWarning(warnInspectiondate(inspectionDate));

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [competentPerson, inspectionDate, anySpecialConditions, specialConditions, uploadedFiles]);

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  const formatAttachments = (files) => {
    return files.map((f) => ({
      amandaCode: parseInt(supportingDocumentCode.code),
      name: f.name,
      attachmentID: f.id,
      description: description,
      status: 'Portal-Submitted',
      category: 'Supporting Documents',
      type: supportingDocumentCode.description,
    }));
  };

  const onRemoveEngineeringPrincipleFile = async (fileId) => {
    const newFiles = uploadedFiles.filter((e) => e.id !== fileId);
    setUploadedFiles(newFiles);
  };

  return (
    <>
      <FormGroup
        id={id + '_competentPerson'}
        label={labels.competentPersonLabel}
        helpMessage={labels.competentPersonHelpText}
        isRequired
        errorMessage={errors[`${id}_competentPerson`]?.errorMessage}
        hasError={showErrors && errors[`${id}_competentPerson`]?.invalid}
        margin={{ top: 'lg' }}
      >
        <Input
          onChange={(e) => {
            setCompetentPerson(e.target.value);
          }}
          inputWidth="xl"
          value={competentPerson}
        />
      </FormGroup>

      {showErrors &&
        validateInspectiondate(inspectionDate) &&
        labels.inspectionDateErrorTitle &&
        labels.inspectionDateErrorText && (
          <InPageAlert variant="error" title={labels.inspectionDateErrorTitle} className={styles.marginTopOneHalf}>
            {sanitizeHtml(labels.inspectionDateErrorText)}
          </InPageAlert>
        )}

      <div id={id + '_inspectionDate'}>
        <FormDate
          id={id + '_inspectionDate'}
          name={id + '_inspectionDate'}
          label={labels.inspectionDateLabel}
          className={styles.inspectionDate}
          onChange={(e) => {
            setInspectionDate(e.value);
          }}
          value={inspectionDate}
          required={true}
          hasError={showErrors && errors[`${id}_inspectionDate`]?.invalid}
          errorMessage={errors[`${id}_inspectionDate`]?.errorMessage}
          validateOnFly={false}
        />
      </div>

      {inspectionDateWarning && <InPageAlert title={inspectionDateWarning} variant="warning" />}

      <FormRadioGroup
        id={id + '_anySpecialConditions'}
        name={id + '_anySpecialConditions'}
        options={radioOptionsYesNo}
        onChange={(v) => {
          setAnySpecialConditions(v);
          if (v === 'no') setSpecialConditions('');
        }}
        value={anySpecialConditions}
        errorMessage={errors[`${id}_anySpecialConditions`]?.errorMessage}
        hasError={showErrors && errors[`${id}_anySpecialConditions`]?.invalid}
        legend={labels.anySpecialConditionsLabel}
        margin={{ top: 'md' }}
      />

      {anySpecialConditions === 'yes' && (
        <FormGroup
          id={id + '_conditions'}
          label={labels.describeConditionsLabel}
          helpMessage={labels.describeConditionsHelpText}
          margin={{ top: 'lg' }}
          errorMessage={errors[`${id}_conditions`]?.errorMessage}
          hasError={showErrors && errors[`${id}_conditions`]?.invalid}
        >
          <Input
            onChange={(e) => {
              setSpecialConditions(e.target.value);
            }}
            value={specialConditions}
          />
        </FormGroup>
      )}

      {(!uploadedFiles || uploadedFiles.length < maxFiles) && (
        <>
          <Heading level={3} id={id + '_upload'} className={styles.supportDocsTitle}>
            {labels.supportingDocumentsLabel}
          </Heading>

          <div className={styles.supportingDocsDetails}>{sanitizeHtml(labels.supportingDocumentsHelpText)}</div>

          <FileUploadPattern
            api={attachmentApi}
            name={id + '_upload'}
            id={id + '_upload'}
            legendText=""
            initialFiles={uploadedFiles}
            onUploaded={(value) => setUploadedFiles(value)}
            onRemoved={(value) => setUploadedFiles(value)}
            maxMBSize={10}
            acceptedFormats=".png,.jpg,.jpeg,.pdf"
            maxFiles={maxFiles}
            errorMessage={errors[`${id}_upload`]?.errorMessage}
          />
        </>
      )}

      {uploadedFiles && uploadedFiles.length > 0 && (
        <Heading level={3} style={{ marginBottom: '1rem', marginTop: '2rem' }}>
          {labels.uploadedFileLabel}:
        </Heading>
      )}
      {uploadedFiles.map((e) => (
        <UploadedItem
          id={e.id.toString()}
          name={e.name}
          size={e.size}
          onRemove={async () => await onRemoveEngineeringPrincipleFile(e.id)}
          key={e.id}
        />
      ))}
    </>
  );
});

InspectionDocumentsUpload.displayName = 'InspectionDocumentsUpload';
